const SELECTORS = {
  root: '[data-stickit-pre-cart-root]',
  dialogComponent: '#stickit-pre-cart-upgrade-dialog',
  heading: '[data-stickit-pre-cart-heading]',
  text: '[data-stickit-pre-cart-text]',
  media: '[data-stickit-pre-cart-media]',
  error: '[data-stickit-pre-cart-error]',
  upgradeButton: '[data-stickit-pre-cart-upgrade-button]',
  declineButton: '[data-stickit-pre-cart-decline-button]',
  closeButton: '[data-stickit-pre-cart-close]',
};

let pending = null;
let choiceMade = false;
let submittingChoice = false;

function getRoot() {
  return document.querySelector(SELECTORS.root);
}

function getDialogComponent() {
  return document.querySelector(SELECTORS.dialogComponent);
}

function getDialog() {
  return getDialogComponent()?.querySelector('dialog');
}

function getVariants(root) {
  if (!root) return [];
  const script = root.querySelector('[data-stickit-current-variants]');
  if (!script?.textContent) return [];

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.error('Stick-It pre-cart: invalid variant JSON', error);
    return [];
  }
}

function getSelectedVariant(root, form) {
  const id = form.querySelector('[name="id"]')?.value;
  if (!id) return null;

  return getVariants(root).find((variant) => variant.id?.toString() === id.toString()) || null;
}

function getQuantity(form) {
  const value = form.querySelector('[name="quantity"]')?.value;
  const quantity = Number.parseInt(value || '1', 10);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function formatMoney(cents) {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents);
  }

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  }).format(cents / 100);
}

function sessionKey(root) {
  return `stickit-pre-cart-shown-${root.dataset.productHandle}`;
}

function shouldPrompt(root, form, productForm) {
  if (!root || !form || !productForm) return false;
  if (form.closest('.quick-add-modal')) return false;
  if (productForm.dataset.productId?.toString() !== root.dataset.productId?.toString()) return false;
  if (sessionStorage.getItem(sessionKey(root)) === 'true') return false;
  if (!root.dataset.upgradeVariantId) return false;

  const selectedVariant = getSelectedVariant(root, form);
  if (!selectedVariant?.available) return false;

  return true;
}

function setButtonsLoading(isLoading) {
  const dialog = getDialog();
  const buttons = dialog?.querySelectorAll(`${SELECTORS.upgradeButton}, ${SELECTORS.declineButton}, ${SELECTORS.closeButton}`) || [];

  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = isLoading;
    button.toggleAttribute('aria-busy', isLoading);
  });
}

function setError(show) {
  const error = getDialog()?.querySelector(SELECTORS.error);
  if (error) error.hidden = !show;
}

function renderPrompt() {
  const root = getRoot();
  const dialog = getDialog();
  if (!root || !dialog || !pending) return false;

  const { currentVariant, quantity } = pending;
  const currentPrice = Number(currentVariant.price || 0);
  const upgradePrice = Number(root.dataset.upgradePrice || 0);
  const unitDelta = Math.max(0, upgradePrice - currentPrice);
  const totalDelta = unitDelta * quantity;

  dialog.querySelector(SELECTORS.heading).textContent = root.dataset.upgradeHeading || `Upgrade to ${root.dataset.upgradeProductTitle}`;
  dialog.querySelector(SELECTORS.text).textContent = root.dataset.upgradeText || `Choose ${root.dataset.upgradeProductTitle} instead.`;

  const media = dialog.querySelector(SELECTORS.media);
  media.replaceChildren();
  if (root.dataset.upgradeImage) {
    const image = document.createElement('img');
    image.src = root.dataset.upgradeImage;
    image.alt = root.dataset.upgradeProductTitle || '';
    image.loading = 'eager';
    image.className = 'stickit-pre-cart-modal__image';
    media.append(image);
  }

  const upgradeLabel = quantity > 1
    ? `Upgrade all ${quantity} for +${formatMoney(totalDelta)}`
    : `Upgrade for +${formatMoney(totalDelta)}`;
  dialog.querySelector(SELECTORS.upgradeButton).textContent = upgradeLabel;
  dialog.querySelector(SELECTORS.declineButton).textContent = `No thanks, add ${root.dataset.productTitle}`;

  setError(false);
  return true;
}

function openPrompt(form, productForm, currentVariant) {
  const root = getRoot();
  const dialogComponent = getDialogComponent();
  const idInput = form.querySelector('[name="id"]');
  if (!root || !dialogComponent || !idInput) return false;

  pending = {
    form,
    productForm,
    idInput,
    originalVariantId: idInput.value,
    currentVariant,
    quantity: getQuantity(form),
  };
  choiceMade = false;
  submittingChoice = false;
  sessionStorage.setItem(sessionKey(root), 'true');

  if (!renderPrompt()) return false;

  dialogComponent.showDialog();

  window.requestAnimationFrame(() => {
    getDialog()?.querySelector(SELECTORS.upgradeButton)?.focus();
  });

  return true;
}

function submitChoice({ upgrade = false } = {}) {
  if (!pending || submittingChoice) return;

  const root = getRoot();
  const { form, idInput, originalVariantId } = pending;
  const upgradeVariantId = root?.dataset.upgradeVariantId;
  const targetVariantId = upgrade && upgradeVariantId ? upgradeVariantId : originalVariantId;

  choiceMade = true;
  submittingChoice = true;
  setButtonsLoading(true);
  setError(false);

  const previousVariantId = idInput.value;

  try {
    idInput.value = targetVariantId;
    form.requestSubmit();
  } finally {
    idInput.value = previousVariantId;
  }
}

document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] [name="add"]') : null;
    if (!button) return;

    const form = button.closest('form[action*="/cart/add"]');
    const root = getRoot();
    const productForm = form?.closest('product-form-component');

    if (!form || !root || !productForm) return;
    if (!shouldPrompt(root, form, productForm)) return;

    const currentVariant = getSelectedVariant(root, form);
    if (!currentVariant) return;

    event.preventDefault();
    event.stopPropagation();

    openPrompt(form, productForm, currentVariant);
  },
  true
);

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest(SELECTORS.upgradeButton)) {
    submitChoice({ upgrade: true });
  }

  if (target.closest(SELECTORS.declineButton)) {
    submitChoice({ upgrade: false });
  }

  if (target.closest(SELECTORS.closeButton)) {
    submitChoice({ dismissed: true });
  }
});

document.addEventListener('dialog:close', () => {
  if (!pending || choiceMade) return;
  submitChoice({ dismissed: true });
});

document.addEventListener('cart:update', () => {
  if (!submittingChoice) return;

  submittingChoice = false;
  pending = null;
  getDialogComponent()?.closeDialog?.();
});

document.addEventListener('cart:error', () => {
  if (!submittingChoice) return;

  submittingChoice = false;
  choiceMade = false;
  setButtonsLoading(false);
  setError(true);
});
